/**
 * Premium MLB-Style Baseball Field - Canvas 2D
 * Highly polished TV broadcast view with smooth animations
 */
import React, { useRef, useEffect, useCallback, useMemo } from 'react';

const BaseballField2D = ({
  ballPosition = { x: 0, y: 2.5, z: -60 },
  ballVisible = false,
  showStrikeZone = true,
  batterSwinging = false,
  hitResult = null,
  ballTrail = [],
  pitcherThrowing = false,
  onCanvasClick
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const frameCount = useRef(0);
  
  // Pre-compute static elements once
  const staticElements = useMemo(() => {
    const stars = Array.from({ length: 120 }, () => ({
      x: Math.random(),
      y: Math.random() * 0.28,
      size: Math.random() * 2 + 0.5,
      twinkle: Math.random() * Math.PI * 2,
      brightness: 0.4 + Math.random() * 0.6
    }));
    
    const crowdColors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#ffffff', '#ecf0f1', '#e91e63', '#00bcd4'];
    const crowd = Array.from({ length: 600 }, () => ({
      x: Math.random(),
      y: Math.random(),
      color: crowdColors[Math.floor(Math.random() * crowdColors.length)],
      size: 2 + Math.random() * 3,
      wave: Math.random() * Math.PI * 2,
      speed: 0.5 + Math.random() * 1.5
    }));
    
    // Grass mowing pattern angles
    const grassStripes = Array.from({ length: 16 }, (_, i) => ({
      y: i * 0.033,
      dark: i % 2 === 0
    }));
    
    return { stars, crowd, grassStripes };
  }, []);

  // Safe ellipse helper that prevents negative radius errors
  const safeEllipse = useCallback((ctx, x, y, radiusX, radiusY, rotation = 0, startAngle = 0, endAngle = Math.PI * 2) => {
    const safeRadiusX = Math.max(0.1, Math.abs(radiusX));
    const safeRadiusY = Math.max(0.1, Math.abs(radiusY));
    ctx.ellipse(x, y, safeRadiusX, safeRadiusY, rotation, startAngle, endAngle);
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    frameCount.current++;
    const frame = frameCount.current;
    
    // Clear with black
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    
    // === NIGHT SKY with depth ===
    const skyGrad = ctx.createLinearGradient(0, 0, 0, H * 0.42);
    skyGrad.addColorStop(0, '#020810');
    skyGrad.addColorStop(0.3, '#0a1628');
    skyGrad.addColorStop(0.6, '#122642');
    skyGrad.addColorStop(1, '#1a3a52');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, W, H * 0.42);
    
    // Twinkling stars with variable brightness
    staticElements.stars.forEach(star => {
      const twinkle = Math.sin(frame * 0.03 + star.twinkle) * 0.3 + 0.7;
      ctx.globalAlpha = twinkle * star.brightness;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(star.x * W, star.y * H, star.size, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;
    
    // === STADIUM LIGHT TOWERS ===
    const lightTowers = [
      { x: W * 0.06, y: H * 0.02, size: 1.2 },
      { x: W * 0.94, y: H * 0.02, size: 1.2 },
      { x: W * 0.02, y: H * 0.15, size: 1.0 },
      { x: W * 0.98, y: H * 0.15, size: 1.0 },
      { x: W * 0.20, y: H * 0.01, size: 0.9 },
      { x: W * 0.80, y: H * 0.01, size: 0.9 },
    ];
    
    lightTowers.forEach(tower => {
      // Tower pole
      ctx.fillStyle = '#2a2a2a';
      ctx.fillRect(tower.x - 3, tower.y, 6, H * 0.18);
      
      // Light bank glow (large)
      const glow = ctx.createRadialGradient(tower.x, tower.y, 0, tower.x, tower.y, 180 * tower.size);
      glow.addColorStop(0, 'rgba(255, 250, 220, 0.35)');
      glow.addColorStop(0.3, 'rgba(255, 248, 200, 0.15)');
      glow.addColorStop(1, 'rgba(255, 250, 220, 0)');
      ctx.fillStyle = glow;
      ctx.fillRect(tower.x - 180 * tower.size, tower.y - 180 * tower.size, 360 * tower.size, 360 * tower.size);
      
      // Light bank (bright rectangle)
      ctx.fillStyle = '#fffadc';
      ctx.shadowColor = '#fffadc';
      ctx.shadowBlur = 25 * tower.size;
      ctx.fillRect(tower.x - 20 * tower.size, tower.y - 8, 40 * tower.size, 16);
      ctx.shadowBlur = 0;
    });
    
    // === UPPER DECK STANDS ===
    const upperDeckGrad = ctx.createLinearGradient(0, H * 0.10, 0, H * 0.36);
    upperDeckGrad.addColorStop(0, '#0d1520');
    upperDeckGrad.addColorStop(0.5, '#151f2e');
    upperDeckGrad.addColorStop(1, '#1a2a3e');
    ctx.fillStyle = upperDeckGrad;
    
    // Curved stands
    ctx.beginPath();
    ctx.moveTo(0, H * 0.36);
    ctx.bezierCurveTo(W * 0.25, H * 0.30, W * 0.75, H * 0.30, W, H * 0.36);
    ctx.lineTo(W, H * 0.10);
    ctx.bezierCurveTo(W * 0.75, H * 0.06, W * 0.25, H * 0.06, 0, H * 0.10);
    ctx.closePath();
    ctx.fill();
    
    // Animated crowd - wave on home run
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(0, H * 0.36);
    ctx.bezierCurveTo(W * 0.25, H * 0.30, W * 0.75, H * 0.30, W, H * 0.36);
    ctx.lineTo(W, H * 0.10);
    ctx.bezierCurveTo(W * 0.75, H * 0.06, W * 0.25, H * 0.06, 0, H * 0.10);
    ctx.closePath();
    ctx.clip();
    
    staticElements.crowd.forEach(person => {
      const waveAmp = hitResult === 'HOMERUN' ? 4 : 0;
      const waveOffset = waveAmp * Math.sin(frame * 0.12 * person.speed + person.wave);
      const px = person.x * W;
      const py = H * 0.12 + person.y * H * 0.22 + waveOffset;
      ctx.fillStyle = person.color;
      ctx.globalAlpha = 0.55 + Math.sin(frame * 0.05 + person.wave) * 0.15;
      ctx.beginPath();
      ctx.arc(px, py, person.size, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;
    ctx.restore();
    
    // Deck railings
    ctx.strokeStyle = '#0f3460';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(0, H * 0.36);
    ctx.bezierCurveTo(W * 0.25, H * 0.30, W * 0.75, H * 0.30, W, H * 0.36);
    ctx.stroke();
    
    // === OUTFIELD WALL (Green Monster style) ===
    const wallGrad = ctx.createLinearGradient(0, H * 0.36, 0, H * 0.46);
    wallGrad.addColorStop(0, '#0a4d27');
    wallGrad.addColorStop(0.5, '#0d5c30');
    wallGrad.addColorStop(1, '#0a4d27');
    ctx.fillStyle = wallGrad;
    ctx.beginPath();
    ctx.moveTo(0, H * 0.36);
    ctx.bezierCurveTo(W * 0.25, H * 0.30, W * 0.75, H * 0.30, W, H * 0.36);
    ctx.lineTo(W, H * 0.46);
    ctx.bezierCurveTo(W * 0.75, H * 0.40, W * 0.25, H * 0.40, 0, H * 0.46);
    ctx.closePath();
    ctx.fill();
    
    // Yellow home run line with glow
    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 6;
    ctx.shadowColor = '#fbbf24';
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.moveTo(0, H * 0.36);
    ctx.bezierCurveTo(W * 0.25, H * 0.30, W * 0.75, H * 0.30, W, H * 0.36);
    ctx.stroke();
    ctx.shadowBlur = 0;
    
    // Distance markers on wall
    ctx.fillStyle = '#fbbf24';
    ctx.font = 'bold 16px "Segoe UI", Arial';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0,0,0,0.8)';
    ctx.shadowBlur = 4;
    ctx.fillText('330', W * 0.10, H * 0.345);
    ctx.fillText('375', W * 0.28, H * 0.32);
    ctx.fillText('400', W * 0.5, H * 0.305);
    ctx.fillText('375', W * 0.72, H * 0.32);
    ctx.fillText('330', W * 0.90, H * 0.345);
    ctx.shadowBlur = 0;
    
    // === WARNING TRACK ===
    const warnGrad = ctx.createLinearGradient(0, H * 0.46, 0, H * 0.50);
    warnGrad.addColorStop(0, '#a67c52');
    warnGrad.addColorStop(1, '#8b6b4a');
    ctx.fillStyle = warnGrad;
    ctx.beginPath();
    ctx.moveTo(0, H * 0.46);
    ctx.bezierCurveTo(W * 0.25, H * 0.40, W * 0.75, H * 0.40, W, H * 0.46);
    ctx.lineTo(W, H * 0.50);
    ctx.bezierCurveTo(W * 0.75, H * 0.44, W * 0.25, H * 0.44, 0, H * 0.50);
    ctx.closePath();
    ctx.fill();
    
    // === OUTFIELD GRASS with mowing pattern ===
    const grassBase = ctx.createLinearGradient(0, H * 0.50, 0, H);
    grassBase.addColorStop(0, '#1a7a2e');
    grassBase.addColorStop(0.5, '#167026');
    grassBase.addColorStop(1, '#125a1e');
    ctx.fillStyle = grassBase;
    ctx.fillRect(0, H * 0.50, W, H * 0.50);
    
    // Mowing stripes (curved toward home)
    staticElements.grassStripes.forEach(stripe => {
      const stripeY = H * 0.50 + stripe.y * H;
      ctx.fillStyle = stripe.dark ? 'rgba(18, 70, 22, 0.35)' : 'rgba(28, 95, 32, 0.25)';
      ctx.fillRect(0, stripeY, W, H * 0.033);
    });
    
    // === INFIELD DIRT ===
    const homeX = W * 0.5, homeY = H * 0.88;
    const dirtGrad = ctx.createRadialGradient(homeX, H * 0.70, 30, homeX, H * 0.70, 300);
    dirtGrad.addColorStop(0, '#d4a574');
    dirtGrad.addColorStop(0.6, '#c99b65');
    dirtGrad.addColorStop(1, '#167026');
    ctx.fillStyle = dirtGrad;
    ctx.beginPath();
    safeEllipse(ctx, homeX, H * 0.70, 300, 180, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Infield grass cutout
    ctx.fillStyle = '#1a7a2e';
    ctx.beginPath();
    safeEllipse(ctx, homeX, H * 0.64, 150, 95, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // === BASE PATHS (chalk lines) ===
    ctx.strokeStyle = '#f5f5dc';
    ctx.lineWidth = 5;
    ctx.shadowColor = 'rgba(0,0,0,0.4)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 2;
    
    const firstX = W * 0.71, firstY = H * 0.67;
    const secondX = homeX, secondY = H * 0.52;
    const thirdX = W * 0.29, thirdY = H * 0.67;
    
    ctx.beginPath();
    ctx.moveTo(homeX, homeY);
    ctx.lineTo(firstX, firstY);
    ctx.lineTo(secondX, secondY);
    ctx.lineTo(thirdX, thirdY);
    ctx.lineTo(homeX, homeY);
    ctx.stroke();
    ctx.shadowBlur = 0;
    
    // Foul lines to outfield
    ctx.strokeStyle = '#f5f5dc';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(homeX, homeY);
    ctx.lineTo(W * 0.03, H * 0.38);
    ctx.moveTo(homeX, homeY);
    ctx.lineTo(W * 0.97, H * 0.38);
    ctx.stroke();
    
    // === BASES (3D effect) ===
    const drawBase = (x, y, size) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.PI / 4);
      
      // Shadow
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.fillRect(-size + 3, -size + 3, size * 2, size * 2);
      
      // Base
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-size, -size, size * 2, size * 2);
      
      // Highlight
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fillRect(-size, -size, size * 2, size * 0.5);
      
      ctx.restore();
    };
    
    drawBase(firstX, firstY, 13);
    drawBase(secondX, secondY, 11);
    drawBase(thirdX, thirdY, 13);
    
    // Home plate (pentagon)
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.beginPath();
    ctx.moveTo(homeX + 3, homeY - 15);
    ctx.lineTo(homeX + 18, homeY + 3);
    ctx.lineTo(homeX + 12, homeY + 13);
    ctx.lineTo(homeX - 9, homeY + 13);
    ctx.lineTo(homeX - 15, homeY + 3);
    ctx.closePath();
    ctx.fill();
    
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(homeX, homeY - 18);
    ctx.lineTo(homeX + 15, homeY);
    ctx.lineTo(homeX + 10, homeY + 10);
    ctx.lineTo(homeX - 10, homeY + 10);
    ctx.lineTo(homeX - 15, homeY);
    ctx.closePath();
    ctx.fill();
    
    // Batter's boxes
    ctx.strokeStyle = '#f5f5dc';
    ctx.lineWidth = 3;
    ctx.strokeRect(homeX - 58, homeY - 25, 40, 60);
    ctx.strokeRect(homeX + 18, homeY - 25, 40, 60);
    
    // === PITCHER'S MOUND ===
    const moundX = homeX, moundY = H * 0.58;
    const moundGrad = ctx.createRadialGradient(moundX, moundY + 5, 5, moundX, moundY + 5, 55);
    moundGrad.addColorStop(0, '#d4a574');
    moundGrad.addColorStop(0.7, '#c99b65');
    moundGrad.addColorStop(1, '#b8895a');
    ctx.fillStyle = moundGrad;
    ctx.beginPath();
    safeEllipse(ctx, moundX, moundY + 5, 55, 28, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Rubber
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 3;
    ctx.fillRect(moundX - 18, moundY - 4, 36, 8);
    ctx.shadowBlur = 0;
    
    // === PITCHER (detailed) ===
    const pitcherY = moundY - 8;
    ctx.save();
    ctx.translate(moundX, pitcherY);
    ctx.scale(0.75, 0.75);
    
    // Legs
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(-14, 22, 12, 38);
    ctx.fillRect(2, 22, 12, 38);
    
    // Cleats
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(-16, 58, 16, 8);
    ctx.fillRect(0, 58, 16, 8);
    
    // Body/Jersey
    const jerseyGrad = ctx.createLinearGradient(-20, -30, 20, 20);
    jerseyGrad.addColorStop(0, '#1e4a7a');
    jerseyGrad.addColorStop(1, '#153d66');
    ctx.fillStyle = jerseyGrad;
    ctx.beginPath();
    safeEllipse(ctx, 0, 0, 22, 32, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Jersey number
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('45', 0, 8);
    
    // Head
    ctx.fillStyle = '#deb887';
    ctx.beginPath();
    ctx.arc(0, -42, 16, 0, Math.PI * 2);
    ctx.fill();
    
    // Cap
    ctx.fillStyle = '#1e4a7a';
    ctx.beginPath();
    ctx.arc(0, -50, 18, Math.PI, 0);
    ctx.fill();
    ctx.fillRect(-18, -50, 36, 6);
    // Brim
    ctx.fillRect(-6, -46, 22, 6);
    
    // Glove hand
    ctx.fillStyle = '#8b4513';
    ctx.beginPath();
    safeEllipse(ctx, -28, -8, 12, 16, -0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // Throwing arm (animated)
    ctx.save();
    ctx.translate(20, -18);
    const throwAngle = pitcherThrowing ? Math.sin(frame * 0.4) * 0.8 : 0;
    ctx.rotate(throwAngle - 0.4);
    ctx.fillStyle = '#deb887';
    ctx.fillRect(-5, 0, 10, 28);
    // Ball in hand during windup
    if (pitcherThrowing) {
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(0, 30, 6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    
    ctx.restore();
    
    // === BATTER (detailed, larger) ===
    const batterX = homeX + 55;
    const batterY = homeY - 35;
    ctx.save();
    ctx.translate(batterX, batterY);
    ctx.scale(1.5, 1.5);
    
    // Legs
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(-14, 28, 12, 42);
    ctx.fillRect(2, 28, 12, 42);
    
    // Cleats
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(-16, 68, 16, 8);
    ctx.fillRect(0, 68, 16, 8);
    
    // Body/Jersey
    const batterJersey = ctx.createLinearGradient(-20, -30, 20, 20);
    batterJersey.addColorStop(0, '#c41e3a');
    batterJersey.addColorStop(1, '#a01830');
    ctx.fillStyle = batterJersey;
    ctx.beginPath();
    safeEllipse(ctx, 0, 5, 24, 35, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Jersey number
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 22px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('27', 0, 15);
    
    // Head
    ctx.fillStyle = '#deb887';
    ctx.beginPath();
    ctx.arc(0, -38, 18, 0, Math.PI * 2);
    ctx.fill();
    
    // Helmet
    ctx.fillStyle = '#c41e3a';
    ctx.beginPath();
    ctx.arc(0, -46, 20, Math.PI, 0.4);
    ctx.fill();
    ctx.fillRect(-22, -46, 10, 24); // Ear flap
    
    // Arms holding bat
    ctx.fillStyle = '#deb887';
    ctx.save();
    ctx.translate(18, -8);
    ctx.rotate(batterSwinging ? -1.8 : 0.3);
    ctx.fillRect(-5, 0, 10, 30);
    ctx.restore();
    
    ctx.fillStyle = '#deb887';
    ctx.save();
    ctx.translate(-12, -4);
    ctx.rotate(batterSwinging ? -1.5 : -0.3);
    ctx.fillRect(-5, 0, 10, 28);
    ctx.restore();
    
    // BAT (detailed)
    ctx.save();
    ctx.translate(22, -28);
    ctx.rotate(batterSwinging ? -2.3 : 0.85);
    
    // Handle (dark wood)
    ctx.fillStyle = '#2d1810';
    ctx.fillRect(-4, 0, 8, 28);
    
    // Grip tape
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(-4, 5, 8, 18);
    
    // Barrel (ash wood gradient)
    const barrelGrad = ctx.createLinearGradient(0, -65, 0, 0);
    barrelGrad.addColorStop(0, '#f5deb3');
    barrelGrad.addColorStop(0.3, '#e8d4a8');
    barrelGrad.addColorStop(0.7, '#deb887');
    barrelGrad.addColorStop(1, '#d2b48c');
    ctx.fillStyle = barrelGrad;
    ctx.beginPath();
    ctx.moveTo(-5, 0);
    ctx.lineTo(-9, -60);
    ctx.lineTo(9, -60);
    ctx.lineTo(5, 0);
    ctx.closePath();
    ctx.fill();
    
    // Barrel end cap
    ctx.fillStyle = '#2d1810';
    ctx.beginPath();
    safeEllipse(ctx, 0, -60, 9, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Bat knob
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.arc(0, 30, 7, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
    ctx.restore();
    
    // === CATCHER (crouched) ===
    ctx.save();
    ctx.translate(homeX, homeY + 35);
    
    // Body (crouched position)
    ctx.fillStyle = '#374151';
    ctx.beginPath();
    safeEllipse(ctx, 0, -12, 22, 25, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Chest protector
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(-24, -28, 48, 30);
    
    // Head/Mask
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.arc(0, -42, 18, 0, Math.PI * 2);
    ctx.fill();
    
    // Mask bars
    ctx.strokeStyle = '#444';
    ctx.lineWidth = 3;
    for (let i = -2; i <= 2; i++) {
      ctx.beginPath();
      ctx.moveTo(i * 7, -56);
      ctx.lineTo(i * 7, -28);
      ctx.stroke();
    }
    for (let j = 0; j < 3; j++) {
      ctx.beginPath();
      ctx.moveTo(-14, -52 + j * 12);
      ctx.lineTo(14, -52 + j * 12);
      ctx.stroke();
    }
    
    // Mitt (detailed)
    ctx.fillStyle = '#8b4513';
    ctx.beginPath();
    safeEllipse(ctx, 0, -16, 26, 32, 0, 0, Math.PI * 2);
    ctx.fill();
    // Mitt pocket
    ctx.fillStyle = '#6b3e12';
    ctx.beginPath();
    safeEllipse(ctx, 0, -14, 18, 24, 0, 0, Math.PI * 2);
    ctx.fill();
    // Mitt webbing
    ctx.fillStyle = '#7a4515';
    ctx.beginPath();
    safeEllipse(ctx, 0, -20, 10, 14, 0, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
    
    // === STRIKE ZONE ===
    if (showStrikeZone && !hitResult) {
      const szX = homeX;
      const szY = homeY - 105;
      const szW = 85;
      const szH = 95;
      
      // Animated glow
      const glowIntensity = 0.5 + Math.sin(frame * 0.08) * 0.2;
      ctx.shadowColor = `rgba(6, 182, 212, ${glowIntensity})`;
      ctx.shadowBlur = 25;
      ctx.strokeStyle = `rgba(6, 182, 212, ${0.6 + glowIntensity * 0.2})`;
      ctx.lineWidth = 3;
      ctx.strokeRect(szX - szW/2, szY - szH/2, szW, szH);
      ctx.shadowBlur = 0;
      
      // Grid
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 1; i < 3; i++) {
        ctx.moveTo(szX - szW/2 + (szW/3) * i, szY - szH/2);
        ctx.lineTo(szX - szW/2 + (szW/3) * i, szY + szH/2);
        ctx.moveTo(szX - szW/2, szY - szH/2 + (szH/3) * i);
        ctx.lineTo(szX + szW/2, szY - szH/2 + (szH/3) * i);
      }
      ctx.stroke();
    }
    
    // === BALL ===
    if (ballVisible && ballPosition) {
      // Safe progress calculation - clamp to valid range
      const rawProgress = (ballPosition.z + 60) / 60;
      const zProgress = Math.max(0, Math.min(2, rawProgress)); // Allow > 1 for outfield flight
      
      // Screen position with perspective
      const ballScreenX = W * 0.5 + (ballPosition.x || 0) * 45 * Math.max(0.5, 0.5 + zProgress * 0.8);
      
      // Y position: ball arcs up toward wall
      // During pitch (zProgress 0-1): move from pitcher to plate
      // During hit (zProgress > 1): arc toward outfield wall
      let baseY;
      if (zProgress <= 1) {
        baseY = H * 0.55 + zProgress * (H * 0.33);
      } else {
        // After contact - ball flies toward wall
        const hitProgress = zProgress - 1;
        baseY = H * 0.88 - hitProgress * H * 0.45; // Move toward wall area
      }
      
      // Height offset - INCREASED for better arc visibility
      const heightScale = zProgress <= 1 ? 22 : 18; // Higher arc after contact
      const ballScreenY = Math.max(H * 0.08, baseY - (ballPosition.y || 0) * heightScale);
      const ballSize = Math.max(6, 8 + Math.min(zProgress, 1) * 12);
      
      // Ball trail - CONTINUOUS LINE instead of dots
      if (ballTrail && ballTrail.length > 1) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Create gradient for trail
        const trailColor = hitResult ? '#ff6b6b' : '#60a5fa';
        
        // Draw trail as a connected line with fading segments
        ctx.beginPath();
        let started = false;
        
        for (let idx = 0; idx < ballTrail.length; idx++) {
          const pos = ballTrail[idx];
          if (!pos) continue;
          
          const trailProgress = Math.max(0, Math.min(2, (pos.z + 60) / 60));
          const trailX = W * 0.5 + (pos.x || 0) * 40 * Math.max(0.5, 0.5 + trailProgress * 0.8);
          
          let trailBaseY;
          if (trailProgress <= 1) {
            trailBaseY = H * 0.55 + trailProgress * (H * 0.33);
          } else {
            const hitProg = trailProgress - 1;
            trailBaseY = H * 0.88 - hitProg * H * 0.45;
          }
          const trailHeightScale = trailProgress <= 1 ? 22 : 18;
          const trailY = Math.max(H * 0.08, trailBaseY - (pos.y || 0) * trailHeightScale);
          
          if (!started) {
            ctx.moveTo(trailX, trailY);
            started = true;
          } else {
            ctx.lineTo(trailX, trailY);
          }
        }
        
        // Draw the line with gradient width
        ctx.strokeStyle = trailColor;
        ctx.lineWidth = hitResult ? 4 : 3;
        ctx.globalAlpha = 0.8;
        ctx.stroke();
        
        // Add glow effect for home runs
        if (hitResult === 'HOMERUN') {
          ctx.strokeStyle = '#fbbf24';
          ctx.lineWidth = 8;
          ctx.globalAlpha = 0.3;
          ctx.stroke();
        }
        
        ctx.globalAlpha = 1;
      }
      
      // Ball shadow (only when near ground)
      if (zProgress <= 1.2 && ballScreenY > H * 0.5) {
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.beginPath();
        safeEllipse(ctx, ballScreenX, homeY + 10, ballSize * 0.5, ballSize * 0.15, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Home run glow
      if (hitResult === 'HOMERUN') {
        ctx.shadowColor = '#fbbf24';
        ctx.shadowBlur = 35;
      }
      
      // Main ball with 3D shading
      const ballGrad = ctx.createRadialGradient(
        ballScreenX - ballSize * 0.3, ballScreenY - ballSize * 0.3, 0,
        ballScreenX, ballScreenY, ballSize
      );
      ballGrad.addColorStop(0, '#ffffff');
      ballGrad.addColorStop(0.5, '#f8f8f8');
      ballGrad.addColorStop(0.8, '#e8e8e8');
      ballGrad.addColorStop(1, '#d0d0d0');
      ctx.fillStyle = ballGrad;
      ctx.beginPath();
      ctx.arc(ballScreenX, ballScreenY, ballSize, 0, Math.PI * 2);
      ctx.fill();
      
      // Red seams
      if (ballSize > 8) {
        ctx.strokeStyle = '#cc0000';
        ctx.lineWidth = Math.max(1, ballSize / 8);
        ctx.beginPath();
        ctx.arc(ballScreenX - ballSize * 0.25, ballScreenY, ballSize * 0.55, -0.7, 0.7);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(ballScreenX + ballSize * 0.25, ballScreenY, ballSize * 0.55, Math.PI - 0.7, Math.PI + 0.7);
        ctx.stroke();
      }
      
      ctx.shadowBlur = 0;
    }
    
    // === UMPIRE ===
    ctx.save();
    ctx.translate(homeX - 65, homeY + 25);
    ctx.scale(0.85, 0.85);
    
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    safeEllipse(ctx, 0, 0, 20, 32, 0, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = '#deb887';
    ctx.beginPath();
    ctx.arc(0, -40, 14, 0, Math.PI * 2);
    ctx.fill();
    
    // Ump mask
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(0, -40, 16, 0.6, Math.PI - 0.6);
    ctx.fill();
    
    ctx.restore();
    
  }, [ballPosition, ballVisible, showStrikeZone, batterSwinging, hitResult, ballTrail, pitcherThrowing, staticElements, safeEllipse]);

  // 60fps animation loop
  useEffect(() => {
    let lastTime = 0;
    const targetFPS = 60;
    const interval = 1000 / targetFPS;
    
    const animate = (currentTime) => {
      animationRef.current = requestAnimationFrame(animate);
      const delta = currentTime - lastTime;
      
      if (delta >= interval) {
        lastTime = currentTime - (delta % interval);
        draw();
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [draw]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const parent = canvas.parentElement;
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <canvas 
      ref={canvasRef}
      onClick={onCanvasClick}
      className="w-full h-full"
      style={{ display: 'block', background: '#000' }}
    />
  );
};

export default BaseballField2D;
